import { hash } from "../libraries/hash/hash";
import { createZip } from "../libraries/zip/zip";
import type { OpenRouterTool } from "../libraries/openrouter/openrouter";
import type { RPCs, AUTH } from "./rpc";
import { compileLogic } from "./policy/logic";
import { fieldsWithLook, readViewConfig } from "./presentation";
import { applyStep, assert, changeKind, createFileStep, editFile, getFile, getFolder, last, Path, stepsToState } from "./state";
import { QuerySchema, Step, Tables, type Index, type Module, type RowData } from "./tables";

import { validate, type Infer } from "../libraries/json-schema/schema";

import {
  action, appShell, headerPicker, breadcrumbs, card, chatBubble, circleAction, dashboard, downloadFile, editor, externalLink,
  composer, entryList, entryRow, filterBar, formField, group, iconAction, label, linkRow, narrowWorkspace, navigation, muted, pageHeading, readable, toolbar, workspace, wrappedCode, expandable, field, nav, region, text, title, type Region,
  createForm, explorerContent, explorerHeader, explorerSidebar, explorerFolders, explorerWorkspace, itemTree, propertyGrid, sourceReferences, splitView, categoryMenu, uploadFiles,
} from "../libraries/ui/components"
import { palette, safeHtml } from "../libraries/ui/html";
import { schemaForm } from "../libraries/ui/schema-form";
import { commentThread } from "../libraries/ui/comments";
import { categoryChildren, categoryNames, commentPath, createItem, effectiveSchema, itemsIn, readComments, readTaxonomy, slug, taxonomyNode } from "./taxonomy";

type rpcs = ReturnType<typeof RPCs>
type ArgType <F extends Function> = F extends ((x: infer T) => any) ? T : never
type ToPromise <P> = P extends Promise <any> ? P : Promise<P> 
type CONNECTION = {
  functions: {[key in keyof rpcs] : (x: ArgType<rpcs[key]>)=> ToPromise<ReturnType<rpcs[key]> > }
  query : <K extends keyof typeof Tables, F extends Index<K>>  (q: {
    table: K,
    field: F,
    value: any,
  }) => Promise<RowData<K>[]>
}

const MAX_AGENT_TURNS = 40
const AGENT_READ_CHARS = 8_000

const pathParameter = {type: "array", items: {type: "string"}}
const offsetParameter = {type: "integer", minimum: 0, description: "Character offset to start reading from"}
const agentTools: OpenRouterTool[] = [
  {type: "function", function: {name: "list_files", description: "List files and folders in the working state", parameters: {type: "object", properties: {path: pathParameter}}}},
  {type: "function", function: {name: "read_file", description: `Read up to ${AGENT_READ_CHARS} characters from a file in the working state`, parameters: {type: "object", properties: {path: pathParameter, offset: offsetParameter}, required: ["path"]}}},
  {type: "function", function: {
    name: "add_item",
    description: "Add an item to a taxonomy subcategory. subcategoryId may be a unique subcategory name or a slash-separated taxonomy path. Required schema fields are initialized with defaults; provide values to override them.",
    parameters: {type: "object", properties: {
      subcategoryId: {type: "string"},
      values: {type: "object", additionalProperties: true},
    }, required: ["subcategoryId"]},
  }},
  {type: "function", function: {
    name: "read_item",
    description: `Find an item by its id and read up to ${AGENT_READ_CHARS} characters of its JSON`,
    parameters: {type: "object", properties: {itemId: {type: "string"}, offset: offsetParameter}, required: ["itemId"]},
  }},
  {type: "function", function: {
    name: "edit_item",
    description: "Find an item by id and replace old text with new text in its JSON. The old text must occur exactly once.",
    parameters: {type: "object", properties: {itemId: {type: "string"}, old: {type: "string"}, new: {type: "string"}}, required: ["itemId", "old", "new"]},
  }},
  {type: "function", function: {
    name: "edit",
    description: "Replace old text with new text in a file in the working state. The old text must occur exactly once.",
    parameters: {type: "object", properties: {path: pathParameter, old: {type: "string"}, new: {type: "string"}}, required: ["path", "old", "new"]},
  }},
  {type: "function", function: {
    name: "create",
    description: "Create a new file with the supplied content in the working state. The file must not already exist.",
    parameters: {type: "object", properties: {path: pathParameter, content: {type: "string"}}, required: ["path", "content"]},
  }},
]

const DEFAULT_AGENT_INSTRUCTIONS = `You maintain an Etindi module: a versioned tree of text files.
Taxonomy.json defines top-level entries in categories and nested entries in subcategories. Every category and subcategory has a description, a schema, and a subcategories object. Every item must be inside a subcategory, never directly under data/ or a top-level category. An item's category is determined by its path under data/, and it must satisfy the schemas of that subcategory and all ancestors.
Every item has a unique string id and a nonempty sourceReferences array. Each source reference has a path to a file under documents/ and a nonempty excerpt. An item may name its parent with parentItem, which references another item's id. Items never declare children.
Inspect only files relevant to the request. Do not inspect .etindi policies or other agents unless an error specifically requires it. File reads are chunked; when truncated, continue from nextOffset.
Prefer add_item, read_item, and edit_item for taxonomy items. add_item initializes inherited schema fields, but you must replace placeholders and provide valid source references before finishing. Use edit for other existing files and create for other new files. Tool changes are staged together in a temporary working copy.
When the task is complete, respond without a tool call. The complete draft will then be committed automatically. If state validation fails, you will receive the error and may continue repairing the draft or explain what help you need.`

const connection ={
  functions: new Proxy({}, {
    get(_target, key) {
      if (typeof key !== "string") return undefined
      return (body: any) => fetch(`/api/v1/rpc/${key}`, {
        method: "POST",
        body: JSON.stringify(body)
      }).then(async response => {
        const text = await response.text()
        if (response.status != 200) {
          let detail: {code?: string, message?: string} = {}
          try { detail = JSON.parse(text) } catch {}
          const error = new Error(detail.message ?? text ?? `Request failed with status ${response.status}`) as Error & {code?: string}
          if (detail.code) error.code = detail.code
          throw error
        }
        return JSON.parse(text)
      })
    }
  }),
  query: (q: Infer<typeof QuerySchema>) => fetch(`/api/v1/query`, {
    method: "POST",
    body: JSON.stringify(q)
  }).then(async res => {
    let tt = await res.text()
    if (res.status != 200) throw new Error(tt ?? `Request failed with status ${res.status}`)
    return JSON.parse(tt)
  })
} as CONNECTION


function credentialsDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("etindi-private", 1)
    request.onupgradeneeded = () => request.result.createObjectStore("credentials")
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function credentialValue(key: string): Promise<unknown> {
  const db = await credentialsDB()
  const request = db.transaction("credentials").objectStore("credentials").get(key)
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function loadAccounts(): Promise<AUTH[]> {
  const value = await credentialValue("accounts")
  return Array.isArray(value) ? value.filter(account => account && typeof account.id === "string" && typeof account.passhash === "string") : []
}

async function saveCredential(value: AUTH) {
  try {
    const accounts = await loadAccounts()
    const next = [...accounts.filter(account => account.id !== value.id), value]
    const db = await credentialsDB()
    const tx = db.transaction("credentials", "readwrite")
    const store = tx.objectStore("credentials")
    store.put(next, "accounts")
    store.put(value.id, "current")
    return await new Promise<boolean>(resolve => {
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => resolve(false)
    })
  } catch { return false }
}

async function clearCurrentCredential(): Promise<void> {
  const db = await credentialsDB()
  const tx = db.transaction("credentials", "readwrite")
  tx.objectStore("credentials").delete("current")
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

async function loadCredential(): Promise<AUTH | undefined> {
  const current = await credentialValue("current")
  if (current && typeof current === "object" && typeof (current as AUTH).id === "string") {
    await saveCredential(current as AUTH)
    return current as AUTH
  }
  if (typeof current !== "string") return undefined
  return (await loadAccounts()).find(account => account.id === current)
}


let user : AUTH = {
  id: "",
  passhash: ""
}




const app = appShell()
const page = app.page
const addStatus = app.status.add
window.addEventListener("unhandledrejection", event => {
  event.preventDefault()
  addStatus(String(event.reason), "error")
})
window.addEventListener("error", event => addStatus(event.message, "error"))

const routeUrl = (path: Path) => "/" + path.map(encodeURIComponent).join("/")
const routePath = () => location.pathname.split("/").filter(Boolean).map(decodeURI)

async function render(path: Path, updateHistory = true){
  const url = routeUrl(path)
  if (updateHistory && location.pathname !== url) {
    history.pushState(null, "", url)
    window.scrollTo(0, 0)
  }

  function modLink(title: string, path: Path){
    return nav(title, () => render(path))
  }

  page.show()
  app.header.show()

  async function getModule(userId: string, moduleName: string){

    let modId = `${userId}/${moduleName}`

    let module = (await connection.query({ table: "modules", field: "id", value: modId }))[0]
    if (!module){
      addStatus("Module not found: " + modId, "error")
      throw new Error("Module not found: " + modId)
    }
    return module
  }

  async function renderState(module: Module | {commit: string} ){

    async function tryStep(msg: string, ...steps: Step[]){

      console.log("trying steps.")
      steps.forEach(s=>console.log(s.old + " => " + s.new))

      assert(moduleId!=undefined, "no module id") 

      for (let step of steps) {
        applyStep(state, step)
      }
      try{
        const commitId = await connection.functions.commit({
          user,
          moduleId,
          expectedHead: module.commit,
          message: msg,
          steps: [ ...failedSteps, ...steps ]
        })
        failedSteps = []
        module.commit = commitId
      }catch(e){
        failedSteps.push(...steps)
        addStatus("Failed to save: " + e, "warn")
        return false
      }
      steps.forEach(step=>fileSubs.get(step.path)?.forEach(cb => cb(step)))
      return true

    }
    let activeView = workspace()

    

    function fileBreadCrumb(fpath: Path){

      let ppat = ["files", ...fpath]
      return breadcrumbs(homeLink, ppat.map((title, i) => ({
        title,
        open: () => render(path[2] === "file" && i === ppat.length - 1 ? path : [...path.slice(0, 2), ...ppat.slice(0, i + 1)]),
      })))
    }

    function showFile(fpath: Path){
      activeView.show()
      let file = getFile(state, fpath)!
      if (file === undefined) {activeView.add(text("File not found: ", fpath.join("/"))); return}
      let area = editor(file, last(last(fpath)!.split(".")))
      area.setAttribute("aria-label", fpath.join("/"))

      activeView.add(
        fileBreadCrumb(fpath),
        text(area),
        toolbar(iconAction("Save", "save", async ()=>{
          let old = file;
          file = area.get();
          if (await tryStep("manual edit", {old, new:file,  path: fpath})) addStatus("File saved", "success")
        }))
      )
    }

    function showFolder(fpath: Path){
      activeView.show()
      const folder = getFolder(state, fpath)
      if (folder === undefined) { activeView.add(fileBreadCrumb(fpath), text("Folder not found")); return }

      const archive = () => {
        const entries: {name: string, content: string}[] = []
        const visit = (current: typeof folder, parent: Path) => {
          for (const name of Object.keys(current.files).sort()) entries.push({name: [...parent, name].join("/"), content: current.files[name]!})
          for (const name of Object.keys(current.folders).sort()) visit(current.folders[name]!, [...parent, name])
        }
        visit(folder, [])
        const name = (fpath.at(-1) ?? stateName).replace(/[^A-Za-z0-9._-]/g, "-") || "module"
        downloadFile(`${name}.zip`, createZip(entries), "application/zip")
      }

      const upload = async (files: File[], directory: boolean) => {
        const changes: Step[] = []
        const seen = new Set<string>()
        for (const file of files) {
          const relative = directory ? file.webkitRelativePath : file.name
          const parts = relative.split("/").filter(Boolean)
          assert(parts.length > 0 && parts.every(part => part !== "." && part !== ".."), `Invalid upload path: ${relative}`)
          const target = [...fpath, ...parts]
          const key = JSON.stringify(target)
          assert(!seen.has(key), `Duplicate upload path: ${relative}`)
          seen.add(key)
          const old = getFile(state, target) ?? null
          const next = await file.text()
          if (old !== next) changes.push({path: target, old, new: next})
        }
        if (!changes.length) { addStatus("Nothing to upload", "warn"); return }
        if (await tryStep(directory ? "Upload folder" : "Upload files", ...changes)) {
          addStatus(`Uploaded ${changes.length} file${changes.length === 1 ? "" : "s"}`, "success")
          showFolder(fpath)
        }
      }

      activeView.add(
        fileBreadCrumb(fpath),
        toolbar(
          iconAction("New file", "plus", ()=>{
            let relPath = prompt("Enter new file name")
            if (!relPath) return
            tryStep("create new file", {path: [...fpath, ...relPath.split("/").filter(x=>x)], old: null, new: ""}).then(()=>render(path))
          }),
          iconAction("New folder", "folder", ()=>{
            let relpath = prompt("Enter new folder name")
            if (!relpath) return
            getFolder(state, [...fpath, ...relpath.split("/").filter(x=>x)], true)
            render(path)
          }),
          action("Download ZIP", archive),
          ...(moduleId ? [uploadFiles(upload)] : []),
        ),
      )
      if (!Object.keys(folder.folders).length && !Object.keys(folder.files).length) activeView.add(muted("This folder is empty."))

      activeView.add(
        entryList(
          ...Object.keys(folder.folders).sort().map(f => entryRow(nav("", () => render([...path.slice(0, 2), "files", ...fpath, f])).replaceChildren(label("folder", f)))),
          ...Object.keys(folder.files).sort().map(f => entryRow(
            nav("", () => render([...path.slice(0, 2), "file", ...fpath, f])).replaceChildren(label("file", f)),
            iconAction("Delete", "trash", async () => { if (confirm(`Delete ${f}?`) && await tryStep("delete file", {path: [...fpath, f], old: folder.files[f]!, new: null})) showFolder(fpath) }),
          )),
        ),
      )
    }

    let state =  await connection.functions.getState({commitId: module.commit})
    const viewConfig = readViewConfig(state)

    const fileSubs = new Map<Path, ((step: Step) => void)[]> ()
    let failedSteps : Step[] = []

    const moduleId = "id" in module ? module.id : undefined

    let stateName = "id" in module ? module.id.split("/")[1]! : "commit:"+ module.commit

    let homeLink = modLink( stateName , path.slice(0,2))



    if (["files", "file", "agents", "timeline"].includes(path[2]!)) {
      const sections = [
        {path: "", label: "Dashboard", icon: "grid"},
        {path: "files", label: "Files", icon: "folder"},
        {path: "agents", label: "Etindi", icon: "agent"},
        {path: "view", label: "Etkom", icon: "sparkles"},
        {path: "timeline", label: "Timeline", icon: "clock"},
      ] as const
      page.add(navigation(sections.map(section => ({
        label: section.label, icon: section.icon,
        href: routeUrl([...path.slice(0, 2), section.path].filter(Boolean)),
        active: path[2] === section.path || (section.path === "files" && path[2] === "file"),
      }))))
    }

    function parseMessage(raw: string | Record<string, any>) {
      try { return typeof raw === "string" ? JSON.parse(raw) : raw }
      catch { return raw }
    }

    function parseToolResult(content: unknown) {
      if (typeof content !== "string") return content
      try { return JSON.parse(content) }
      catch { return content }
    }

    function toolBatch(response: Record<string, any>, results: Record<string, any>[]) {
      const calls: any[] = response.tool_calls ?? []
      const names = calls.map(call => call.function.name)
      const summary = `Agent used ${calls.length} tool${calls.length === 1 ? "" : "s"} · ${names.join(", ")}`
      return card(expandable(summary, view => view.add(
        response.content ? readable(response.content) : "",
        entryList(...calls.map(call => {
          const result = results.find(item => item.tool_call_id === call.id)
          let args: unknown = call.function.arguments
          try { args = JSON.parse(call.function.arguments) } catch {}
          const path = typeof args === "object" && args !== null && Array.isArray((args as any).path)
            ? (args as any).path.join("/") || "/"
            : undefined
          const subject = path ?? (typeof args === "object" && args !== null
            ? (args as any).itemId ?? (args as any).subcategoryId
            : undefined)
          return expandable(subject ? `${call.function.name} · ${subject}` : call.function.name, details => details.add(
            muted("Arguments"),
            wrappedCode(args),
            result ? muted("Result") : "",
            result ? wrappedCode(parseToolResult(result.content)) : "",
          ))
        })),
      )))
    }

    function chatMessage(raw: string | Record<string, any>) {
      const item = parseMessage(raw)
      if (!item || typeof item !== "object") return card(wrappedCode(item))
      const role = item.role === "user" ? "You" : item.role === "assistant" ? "Agent" : item.role === "tool" ? "Tool result" : "Instructions"
      if (item.role === "tool" || item.role === "system") return expandable(role, view => view.add(wrappedCode(item.content)))
      if (item.tool_calls?.length) return toolBatch(item, [])
      return chatBubble(
        role,
        typeof item.content === "string" && item.content ? readable(item.content) : muted("Using tools…"),
        item.role === "user",
      )
    }

    function conversationMessages(rawMessages: string[]) {
      const views = []
      for (let index = 0; index < rawMessages.length; index++) {
        const message = parseMessage(rawMessages[index]!)
        if (!message || typeof message !== "object" || !message.tool_calls?.length) {
          views.push(chatMessage(message))
          continue
        }
        const callIds = new Set(message.tool_calls.map((call: any) => call.id))
        const results: Record<string, any>[] = []
        while (index + 1 < rawMessages.length) {
          const next = parseMessage(rawMessages[index + 1]!)
          if (!next || typeof next !== "object" || next.role !== "tool" || !callIds.has(next.tool_call_id)) break
          results.push(next)
          index++
        }
        views.push(toolBatch(message, results))
      }
      return views
    }

    async function runAgentDraft(moduleId: string, agentId: string, output: Region, userPrompt: string, triggerCommit = module.commit){
      const runId = crypto.randomUUID().slice(0, 8)
      console.info(`[agent:${runId}] run started`, {moduleId, agentId, triggerCommit, userPrompt})
      const working = structuredClone(state)
      const steps: Step[] = []
      const messageSteps: Step[] = []
      const editSteps: Step[] = []
      const config = JSON.parse(getFile(working, [".etindi", "agents", agentId, "agent.json"])!)
      assert(typeof config.model === "string", "Agent model not found")
      const folder = getFolder(working, [".etindi", "agents", agentId, "messages"])!
      const messages = Object.entries(folder.files).sort(([a], [b])=>a.localeCompare(b)).map(([, value])=>JSON.parse(value))

      const readChunk = (content: string, requestedOffset: unknown) => {
        const offset = requestedOffset ?? 0
        assert(Number.isSafeInteger(offset) && Number(offset) >= 0, "offset must be a non-negative integer")
        assert(Number(offset) <= content.length, `offset exceeds file length ${content.length}`)
        const end = Math.min(content.length, Number(offset) + AGENT_READ_CHARS)
        return {
          content: content.slice(Number(offset), end),
          offset: Number(offset),
          nextOffset: end < content.length ? end : null,
          totalLength: content.length,
          truncated: end < content.length,
        }
      }
      const itemMatches = (itemId: unknown) => {
        assert(typeof itemId === "string" && itemId.length > 0, "itemId must be a non-empty string")
        const matches: {path: Path, content: string}[] = []
        const visit = (current: NonNullable<ReturnType<typeof getFolder>>, parent: Path) => {
          for (const [name, content] of Object.entries(current.files)) {
            if (!name.endsWith(".json")) continue
            try {
              if (JSON.parse(content).id === itemId) matches.push({path: [...parent, name], content})
            } catch {}
          }
          for (const [name, child] of Object.entries(current.folders)) visit(child, [...parent, name])
        }
        const data = getFolder(working, ["data"])
        if (data) visit(data, ["data"])
        return matches
      }
      const findItem = (itemId: unknown) => {
        const matches = itemMatches(itemId)
        assert(matches.length === 1, matches.length ? `Item id is ambiguous: ${String(itemId)}` : `Item not found: ${String(itemId)}`)
        return matches[0]!
      }
      const findSubcategoryPath = (subcategoryId: unknown): Path => {
        assert(typeof subcategoryId === "string" && subcategoryId.length > 0, "subcategoryId must be a non-empty string")
        const root = readTaxonomy(working)
        const paths: Path[] = []
        const visit = (nodes: Record<string, any>, parent: Path) => {
          for (const [name, node] of Object.entries(nodes)) {
            const path = [...parent, name]
            if (path.length >= 2 && (name === subcategoryId || path.join("/") === subcategoryId)) paths.push(path)
            visit(node.subcategories ?? {}, path)
          }
        }
        visit(root.categories, [])
        assert(paths.length === 1, paths.length ? `Subcategory id is ambiguous: ${subcategoryId}; use its full path` : `Subcategory not found: ${subcategoryId}`)
        return paths[0]!
      }

      function appendMessage(message: any){
        const last = Math.max(-1, ...Object.keys(folder.files).map(name=>Number(name.slice(0, -5))))
        const path = [".etindi", "agents", agentId, "messages", `${String(last + 1).padStart(12, "0")}.json`]
        const added: Step[] = [{path, old: null, new: JSON.stringify(message, null, 2)}]
        added.forEach(step=>applyStep(working, step))
        steps.push(...added)
        messageSteps.push(...added)
        messages.push(message)
      }

      async function submit(author: "user" | "agent", message: string){
        console.info(`[agent:${runId}] committing`, {author, message, edits: editSteps.length, messages: messageSteps.length})
        if (author === "user") {
          module.commit = await connection.functions.commit({user, moduleId, expectedHead: module.commit, message, steps})
        } else {
          if (editSteps.length) {
            module.commit = await connection.functions.commitAsAgent({
              user, moduleId, agentId, triggerCommit,
              expectedHead: module.commit, message, steps: editSteps
            })
          }
          if (messageSteps.length) {
            module.commit = await connection.functions.commit({
              user, moduleId, expectedHead: module.commit,
              message, steps: messageSteps
            })
          }
        }
        state = await connection.functions.getState({commitId:module.commit})
        console.info(`[agent:${runId}] committed`, {head: module.commit})
      }

      async function attemptAgentCommit(message: string): Promise<"committed" | "invalid" | "paused"> {
        try {
          await submit("agent", message)
          output.add(text("Committed: " + message))
          return "committed"
        } catch (cause) {
          const error = cause as Error & {code?: string}
          console.warn(`[agent:${runId}] commit failed`, {code: error.code, message: error.message})
          if (error.code === "UPDATE_DENIED") {
            const commitMessage = field(message)
            output.add(card(
              text("Agent permission denied: " + error.message),
              commitMessage,
              action("commit as user", async ()=>{
                try {
                  await submit("user", commitMessage.get() || "Agent-assisted edit")
                  await render(path)
                  addStatus("Agent draft committed as user", "success")
                } catch (error) { addStatus(String(error), "error") }
              })
            ))
            return "paused"
          } else if (error.code === "INVALID_STATE") {
            const feedback = {
              role: "system",
              content: `The attempted commit failed state validation: ${error.message}. Continue editing to repair the draft, or explain what help you need from the user.`,
            }
            appendMessage(feedback)
            output.add(chatMessage(feedback))
            addStatus("Agent draft is not yet valid: " + error.message, "warn")
            return "invalid"
          } else throw error
        }
      }

      const commitMessage = `Agent run: ${userPrompt}`
      let repairing = false
      for (let turn = 0; turn < MAX_AGENT_TURNS; turn++) {
        const started = performance.now()
        console.info(`[agent:${runId}] inference requested`, {turn: turn + 1, model: config.model, messages: messages.length})
        let response: any
        try {
          response = await connection.functions.requestLLM({
            user, model: config.model, messages, tools: agentTools
          })
        } catch (error) {
          console.error(`[agent:${runId}] inference failed`, {turn: turn + 1, durationMs: Math.round(performance.now() - started), error})
          throw error
        }
        console.info(`[agent:${runId}] inference completed`, {
          turn: turn + 1,
          durationMs: Math.round(performance.now() - started),
          contentLength: typeof response.content === "string" ? response.content.length : 0,
          tools: (response.tool_calls ?? []).map((call: any) => call.function?.name),
        })
        console.debug(`[agent:${runId}] inference response`, response)
        appendMessage(response)
        const calls: any[] = response.tool_calls ?? []
        if (calls.length === 0) {
          output.add(chatMessage(response))
          if (repairing) {
            if (messageSteps.length) {
              module.commit = await connection.functions.commit({
                user, moduleId, expectedHead: module.commit, message: commitMessage, steps: messageSteps,
              })
              state = await connection.functions.getState({commitId: module.commit})
            }
            addStatus("The agent needs help completing an invalid draft.", "warn")
            return
          }
          const status = await attemptAgentCommit(commitMessage)
          if (status === "invalid") { repairing = true; continue }
          return
        }

        const toolMessages: Record<string, any>[] = []
        for (const call of calls) {
          const args = JSON.parse(call.function.arguments)
          console.info(`[agent:${runId}] tool started`, {turn: turn + 1, tool: call.function.name, args})
          let result: any
          try {
            if (call.function.name === "list_files") {
              const target = getFolder(working, args.path ?? [])
              assert(target !== undefined, "Folder not found")
              result = {files: Object.keys(target.files), folders: Object.keys(target.folders)}
            } else if (call.function.name === "read_file") {
              const content = getFile(working, validate(Path, args.path))
              assert(content !== undefined, "File not found")
              result = readChunk(content, args.offset)
            } else if (call.function.name === "add_item") {
              const categoryPath = findSubcategoryPath(args.subcategoryId)
              const values = args.values ?? {}
              assert(typeof values === "object" && values !== null && !Array.isArray(values), "values must be an object")
              const item = {...createItem(effectiveSchema(working, readTaxonomy(working), categoryPath), typeof values.title === "string" ? values.title : "Untitled"), ...values}
              assert(typeof item.id === "string" && item.id.length > 0, "item id must be a non-empty string")
              assert(itemMatches(item.id).length === 0, `Item id already exists: ${item.id}`)
              const filename = slug(item.id)
              assert(filename.length > 0, "item id must contain letters or numbers")
              const path = ["data", ...categoryPath, `${filename}.json`]
              const step = createFileStep(working, path, JSON.stringify(item, null, 2))
              applyStep(working, step)
              steps.push(step)
              editSteps.push(step)
              repairing = false
              result = {ok: true, path, item}
            } else if (call.function.name === "read_item") {
              const item = findItem(args.itemId)
              result = {path: item.path, ...readChunk(item.content, args.offset)}
            } else if (call.function.name === "edit_item") {
              const item = findItem(args.itemId)
              assert(typeof args.old === "string", "old must be a string")
              assert(typeof args.new === "string", "new must be a string")
              const step = editFile(working, item.path, args.old, args.new)
              applyStep(working, step)
              steps.push(step)
              editSteps.push(step)
              repairing = false
              result = {ok: true, path: item.path}
            } else if (call.function.name === "edit") {
              const path = validate(Path, args.path)
              assert(typeof args.old === "string", "old must be a string")
              assert(typeof args.new === "string", "new must be a string")
              const step = editFile(working, path, args.old, args.new)
              applyStep(working, step)
              steps.push(step)
              editSteps.push(step)
              repairing = false
              result = {ok: true}
            } else if (call.function.name === "create") {
              const path = validate(Path, args.path)
              assert(typeof args.content === "string", "content must be a string")
              const step = createFileStep(working, path, args.content)
              applyStep(working, step)
              steps.push(step)
              editSteps.push(step)
              repairing = false
              result = {ok: true}
            } else throw new Error("Unknown tool: " + call.function.name)
          } catch (error) {
            result = {error: String(error)}
          }
          console.info(`[agent:${runId}] tool completed`, {turn: turn + 1, tool: call.function.name, result})
          const toolMessage = {role: "tool", tool_call_id: call.id, content: JSON.stringify(result)}
          appendMessage(toolMessage)
          toolMessages.push(toolMessage)
        }
        output.add(toolBatch(response, toolMessages))
      }

      const notice = {
        role: "system",
        content: `The agent stopped after reaching the ${MAX_AGENT_TURNS}-turn limit.`,
      }
      appendMessage(notice)
      output.add(chatMessage(notice))
      const status = await attemptAgentCommit(commitMessage)
      if (status === "invalid" && messageSteps.length) {
        module.commit = await connection.functions.commit({
          user, moduleId, expectedHead: module.commit, message: commitMessage, steps: messageSteps,
        })
        state = await connection.functions.getState({commitId: module.commit})
      }
      addStatus(notice.content, "warn")
    }

    async function showAgents(fpath: Path){
      activeView.show()
      const agentsPath = [...path.slice(0, 2), "agents"]
      const folder = getFolder(state, [".etindi", "agents"])
      const ids = Object.keys(folder?.folders ?? {})
      const commits = await connection.functions.getHistory({commit: module.commit})
      const activity = new Map<string, number>()
      for (const commit of commits) {
        for (const step of commit.steps) {
          const id = step.path[0] === ".etindi" && step.path[1] === "agents" ? step.path[2] : undefined
          if (id && ids.includes(id)) activity.set(id, commit.timestamp)
        }
      }
      const orderedIds = ids.sort((left, right) => (activity.get(right) ?? 0) - (activity.get(left) ?? 0))
      const agentId = fpath[0] === "history" ? undefined : (fpath[0] ?? orderedIds[0])
      const createChat = async (systemMessage = DEFAULT_AGENT_INSTRUCTIONS) => {
        if (!moduleId) return
        const created = await connection.functions.createAgent({
          user, moduleId, expectedHead: module.commit,
          model: "z-ai/glm-5.3-flash", systemMessage,
        })
        module.commit = created.head
        state = await connection.functions.getState({commitId: module.commit})
        await render([...agentsPath, created.id])
      }

      if (agentId){
        const agentFolder = getFolder(state, [".etindi", "agents", agentId])
        const config = getFile(state, [".etindi", "agents", agentId, "agent.json"])
        if (!agentFolder || !config) { activeView.add(text("Agent not found")); page.add(activeView); return }
        const messagesFolder = agentFolder.folders.messages
        const names = Object.keys(messagesFolder?.files ?? {}).sort()
        const output = region()
        const agentConfig = JSON.parse(config) as {model: string, restrictions?: string[]}
        const restrictionFields = (agentConfig.restrictions ?? []).map(rule => field(rule))
        const restrictionsView = region()
        const drawRestrictions = () => restrictionsView.show(
          ...restrictionFields.map((rule, index) => text(
            rule,
            action("remove", () => { restrictionFields.splice(index, 1); drawRestrictions() }),
          )),
          action("add rule", () => {
            restrictionFields.push(field('step["path"][0] = "data"'))
            drawRestrictions()
          }),
          action("save restrictions", async () => {
            try {
              const restrictions = restrictionFields.map(rule => rule.get().trim()).filter(Boolean)
              restrictions.forEach(compileLogic)
              const next = JSON.stringify({...agentConfig, restrictions}, null, 2)
              if (await tryStep("Update agent restrictions", {
                path: [".etindi", "agents", agentId, "agent.json"], old: config, new: next,
              })) await render(path)
            } catch (error) { addStatus(String(error), "error") }
          }),
        )
        drawRestrictions()
        const restrictionCount = agentConfig.restrictions?.length ?? 0
        const restrictions = expandable(
          "Restrictions",
          view => view.add(moduleId
            ? restrictionsView
            : entryList(...(agentConfig.restrictions ?? []).map(rule => text(rule))),
          ),
          `${restrictionCount} required rule${restrictionCount === 1 ? "" : "s"}`,
        )
        activeView.add(
          toolbar(
            iconAction("All conversations", "agent", () => render([...agentsPath, "history"])),
            moduleId ? iconAction("New chat", "plus", () => createChat()) : region(),
          ).setAttribute("class", "etindi-chat-navigation"),
          pageHeading("Conversation", "Work with an agent on this module.", "agent"),
          muted(agentId),
          restrictions,
          ...conversationMessages(names.map(name => messagesFolder!.files[name]!)),
          output,
          moduleId ? composer(async value => {
            const currentNames = Object.keys(getFolder(state, [".etindi", "agents", agentId, "messages"])?.files ?? {})
            const last = Math.max(-1, ...currentNames.map(name=>Number(name.slice(0, -5))))
            const messagePath = [".etindi", "agents", agentId, "messages", `${String(last + 1).padStart(12, "0")}.json`]
            const item = {role: "user", content: value}
            if (!await tryStep("Send agent message", {path: messagePath, old: null, new: JSON.stringify(item, null, 2)})) throw new Error("Message was not saved.")
            output.add(chatMessage(item))
            await runAgentDraft(moduleId, agentId, output, value)
          }, error => addStatus(String(error), "error")) : "",
        )
      }else{
        const instructions = field(DEFAULT_AGENT_INSTRUCTIONS)
        activeView.add(
          pageHeading("Agents", "Continue a conversation or start a new one.", "agent"),
          moduleId ? iconAction("New chat", "plus", () => createChat(instructions.get())) : "",
          orderedIds.length ? entryList(...orderedIds.map(id => nav("Conversation · " + id, () => render([...agentsPath, id])))) : muted("No conversations yet. Start a new chat to work with an agent."),
        )
      }
      page.add(activeView)
    }

    async function showTimeline(fpath: Path){
      let history = await connection.functions.getHistory({commit: module.commit})
      const results = region()
      const authors = [...new Set(history.map(commit => commit.author))].sort()
      const files = [...new Set(history.flatMap(commit => commit.steps.map(step => step.path.join("/"))))].sort()
      const folders = [...new Set(history.flatMap(commit => commit.steps.flatMap(step =>
        step.path.slice(0, -1).map((_, index) => step.path.slice(0, index + 1).join("/"))
      )))].sort()
      const paths = [...new Set([...folders, ...files])].sort()

      function commitView(commit: typeof history[number]) {
        const summary = `${commit.message}${commit.id === module.commit ? " · Current" : ""}`
        return expandable(summary, view => view.add(
          text(`${commit.steps.length} file changes`),
          entryList(...commit.steps.map(step => expandable(
            `${changeKind(step)} ${step.path.join("/")}`,
            details => details.add(
              muted("Before"),
              step.old === null ? muted("File did not exist.") : wrappedCode(step.old),
              muted("After"),
              step.new === null ? muted("File was deleted.") : wrappedCode(step.new),
            ),
          ))),
          externalLink("view", "/commit/" + commit.id),
          moduleId && commit.id !== module.commit ? action("revert to this commit", async () => {
            if (!confirm(`Revert ${moduleId} to ${commit.id}?`)) return
            try {
              const target = await connection.functions.getState({commitId: commit.id})
              module.commit = await connection.functions.commit({
                user,
                moduleId,
                expectedHead: module.commit,
                message: `Revert to ${commit.id}`,
                steps: stepsToState(state, target),
              })
              await render([...moduleId.split("/"), "timeline"])
              addStatus("Revert committed", "success")
            } catch (error) {
              addStatus(String(error), "error")
            }
          }) : "",
        ), `${new Date(commit.timestamp).toLocaleString()} · ${commit.author}`)
      }

      function showResults(selection: Record<string, string>) {
        const author = selection.author?.toLocaleLowerCase() ?? ""
        let pathPattern: RegExp | undefined
        try { pathPattern = selection.path ? new RegExp(selection.path, "i") : undefined }
        catch {
          results.show(muted("Enter a valid path regular expression."))
          return
        }
        const filtered = history.filter(commit =>
          (!author || commit.author.toLocaleLowerCase().includes(author)) &&
          (!pathPattern || commit.steps.some(step => [
            step.path.join("/"),
            ...step.path.slice(0, -1).map((_, index) => step.path.slice(0, index + 1).join("/")),
          ].some(path => pathPattern.test(path))))
        ).slice().reverse()
        results.show(
          muted(`${filtered.length} of ${history.length} commits`).style({marginBottom: "8px"}),
          filtered.length ? entryList(...filtered.map(commitView)) : muted("No commits match these filters."),
        )
      }

      activeView.show(
        pageHeading("Timeline", "Review commits and inspect their file changes.", "clock"),
        filterBar([
          {key: "author", label: "Author", placeholder: "Search authors…", values: authors},
          {
            key: "path", label: "File or folder", placeholder: "Search paths or enter a regex…", values: paths,
            // hint: "Examples: data/ · \\.json$ · ^.etindi/agents",
          },
        ], showResults),
        results,
      )
      showResults({author: "", path: ""})
      page.add(activeView)
    }

    async function showTaxonomy(viewPath: Path) {
      activeView = explorerWorkspace()
      const root = readTaxonomy(state)
      const base = [...path.slice(0, 2), "view"]
      const itemPath = ["data", ...viewPath]
      const itemSource = getFile(state, itemPath)
      const categoryPath = itemSource === undefined ? viewPath : viewPath.slice(0, -1)

      const open = (target: Path) => render([...base, ...target])
      const categoryLabel = (name: string) => name.replaceAll("-", " ").replace(/\b\w/g, letter => letter.toUpperCase())
      const presentedFields = (value: Record<string, unknown>, look: "title" | "header" | "default" | "html") => fieldsWithLook(value, viewConfig, look).filter(([field]) => field !== "id")
      const fieldText = (entry: [string, unknown] | undefined) => {
        const value = entry?.[1]
        return typeof value === "string" || typeof value === "number" || typeof value === "boolean" ? String(value) : undefined
      }
      const sourceList = (value: unknown) => Array.isArray(value) ? value.flatMap(reference => {
        if (!reference || typeof reference !== "object") return []
        const {path, excerpt} = reference as {path?: unknown, excerpt?: unknown}
        return Array.isArray(path) && path.every(part => typeof part === "string") && typeof excerpt === "string"
          ? [{path: path as string[], excerpt}]
          : []
      }) : []
      const filePaths = (folderPath: Path): Path[] => {
        const result: Path[] = []
        const visit = (folder: NonNullable<ReturnType<typeof getFolder>>, parent: Path) => {
          for (const name of Object.keys(folder.files).sort()) result.push([...parent, name])
          for (const [name, child] of Object.entries(folder.folders).sort(([a], [b]) => a.localeCompare(b))) visit(child, [...parent, name])
        }
        const folder = getFolder(state, folderPath)
        if (folder) visit(folder, folderPath)
        return result
      }
      const allItemIds = () => filePaths(["data"]).flatMap(target => {
        try {
          const value = JSON.parse(getFile(state, target)!)
          return typeof value.id === "string" ? [value.id] : []
        } catch { return [] }
      })
      const formOptions = (excludeItemId?: string) => ({
        hidden: ["id"],
        choices: {
          "document-path": filePaths(["documents"]),
          "item-id": allItemIds().filter(id => id !== excludeItemId),
        },
      })
      const itemId = (value: Record<string, unknown>) => typeof value.id === "string" ? value.id : undefined
      const parentId = (value: Record<string, unknown>) => typeof value.parentItem === "string" ? value.parentItem : undefined
      const nestedItems = (items: typeof categoryItems) => {
        const ids = new Set(items.flatMap(item => itemId(item.value) ?? []))
        const children = new Map<string | undefined, typeof items>()
        for (const item of items) {
          const parent = parentId(item.value)
          const key = parent && ids.has(parent) ? parent : undefined
          children.set(key, [...(children.get(key) ?? []), item])
        }
        const rows: {item: typeof items[number], depth: number}[] = []
        const visit = (parent: string | undefined, depth: number) => {
          for (const item of children.get(parent) ?? []) {
            rows.push({item, depth})
            visit(itemId(item.value), depth + 1)
          }
        }
        visit(undefined, 0)
        return rows
      }
      const crumbs = (target: Path) => breadcrumbs(homeLink, [
        {title: "ETKOM", open: () => open([])},
        ...target.map((name, index) => ({title: categoryLabel(name.replace(/\.json$/, "")), open: () => open(target.slice(0, index + 1))})),
      ])

      const categories = categoryNames(root, [])
      const menu = categories.map(name => ({
        label: categoryLabel(name), active: categoryPath[0] === name, open: () => open([name]),
      }))
      const categoryItems = itemsIn(state, categoryPath)
      const statuses = [...new Set(categoryItems.flatMap(({value}) => typeof value.status === "string" ? [value.status] : []))].sort()
      let updateListing = (_filters: {status: string, search: string}) => {}
      const sidebar = explorerSidebar(stateName, categoryMenu(menu), statuses, filters => updateListing(filters))

      const content = region()
      activeView.show(
        splitView(sidebar, explorerContent(content)),
      )

      const changeTaxonomy = async (parentPath: Path, title: string) => {
        if (parentPath.length >= 2) throw new Error("Subcategories cannot contain additional subcategories.")
        const name = slug(title)
        if (!name) throw new Error("Enter a category name containing letters or numbers.")
        const old = getFile(state, ["taxonomy.json"])
        const next = structuredClone(root)
        const children = categoryChildren(next, parentPath)
        if (children[name]) throw new Error("That category already exists.")
        children[name] = {
          description: "",
          schema: {type: "object"},
          subcategories: {},
        }
        if (await tryStep(`Add category ${title}`, {path: ["taxonomy.json"], old: old ?? null, new: JSON.stringify(next, null, 2)})) {
          await open([...parentPath, name])
        }
      }

      if (itemSource !== undefined) {
        let item: Record<string, unknown>
        try { item = JSON.parse(itemSource) }
        catch { content.show(crumbs(viewPath), text("This item is not valid JSON.")); page.add(activeView); return }
        const titleFields = presentedFields(item, "title")
        const headerFields = presentedFields(item, "header")
        const defaultFields = presentedFields(item, "default").filter(([property]) => property !== "sourceReferences" && property !== "parentItem")
        const htmlFields = presentedFields(item, "html")
        const name = fieldText(titleFields[0]) ?? categoryLabel(viewPath.at(-1)!.replace(/\.json$/, ""))
        const heading = fieldText(headerFields[0]) ?? itemPath.join("/")
        const itemsById = new Map(categoryItems.flatMap(entry => {
          const id = itemId(entry.value)
          return id ? [[id, entry] as const] : []
        }))
        const parentChain: typeof categoryItems = []
        const seenParents = new Set<string>()
        let parent = parentId(item)
        while (parent && !seenParents.has(parent)) {
          seenParents.add(parent)
          const entry = itemsById.get(parent)
          if (!entry) break
          parentChain.unshift(entry)
          parent = parentId(entry.value)
        }
        const itemCrumbs = breadcrumbs(nav(stateName, () => open([])), [
          ...categoryPath.map((part, index) => ({title: categoryLabel(part), open: () => open(categoryPath.slice(0, index + 1))})),
          ...parentChain.map(entry => ({
            title: fieldText(presentedFields(entry.value, "title")[0]) ?? categoryLabel(entry.path.at(-1)!.replace(/\.json$/, "")),
            open: () => open(entry.path.slice(1)),
          })),
          {title: name, open: () => open(viewPath)},
        ])
        const descendantIds = new Set(itemId(item) ? [itemId(item)!] : [])
        let foundDescendant = true
        while (foundDescendant) {
          foundDescendant = false
          for (const entry of categoryItems) {
            const id = itemId(entry.value)
            const parent = parentId(entry.value)
            if (id && parent && descendantIds.has(parent) && !descendantIds.has(id)) {
              descendantIds.add(id)
              foundDescendant = true
            }
          }
        }
        const childRows = nestedItems(categoryItems.filter(entry => {
          const id = itemId(entry.value)
          return id ? id !== itemId(item) && descendantIds.has(id) : false
        }))
        const edit = region()
        const comments = readComments(state, itemPath)
        content.show(
          itemCrumbs,
          pageHeading(name, heading, "file"),
          moduleId ? toolbar(iconAction("Edit item", "edit", () => {
            const schema = effectiveSchema(state, root, categoryPath)
            edit.show(schemaForm(
            schema, item,
            async next => {
              validate({json: schema as any}, next)
              if (!await tryStep(`Edit item ${name}`, {path: itemPath, old: itemSource, new: JSON.stringify(next, null, 2)})) throw new Error("The item was not saved.")
              await open(viewPath)
              addStatus("Item saved", "success")
            },
            () => edit.show(),
            error => addStatus(String(error), "error"),
            formOptions(itemId(item)),
          ))})) : "",
          edit,
          ...titleFields.slice(1).map(entry => title(fieldText(entry) ?? entry[0])),
          ...headerFields.slice(1).map(entry => title(fieldText(entry) ?? entry[0])),
          defaultFields.length ? card(propertyGrid(defaultFields.map(([property, value]) => ({name: property, value})))) : "",
          sourceList(item.sourceReferences).length ? sourceReferences(sourceList(item.sourceReferences), target => render([...path.slice(0, 2), "file", ...target])) : "",
          ...htmlFields.map(([property, value]) => card(
            title(categoryLabel(property)).style({fontSize: "15px", marginTop: "0"}),
            typeof value === "string" ? safeHtml(value) : wrappedCode(value),
          )),
          childRows.length ? group(
            title("Items").style({fontSize: "18px"}),
            itemTree(childRows.map(({item: child}) => ({
              id: itemId(child.value)!,
              ...(parentId(child.value) && descendantIds.has(parentId(child.value)!) ? {parentId: parentId(child.value)} : {}),
              name: fieldText(presentedFields(child.value, "title")[0]) ?? categoryLabel(child.path.at(-1)!.replace(/\.json$/, "")),
              open: () => open(child.path.slice(1)),
              ...(readComments(state, child.path).length ? {meta: String(readComments(state, child.path).length)} : {}),
            }))),
          ).setAttribute("class", "etindi-child-items") : "",
          commentThread(comments, moduleId && user.id ? async comment => {
            const target = commentPath(itemPath)
            const old = getFile(state, target)
            const next = [...comments, {author: user.id, content: comment, timestamp: Date.now()}]
            if (!await tryStep(`Comment on ${name}`, {path: target, old: old ?? null, new: JSON.stringify(next, null, 2)})) throw new Error("The comment was not saved.")
            await open(viewPath)
          } : undefined),
        )
      } else {
        const node = taxonomyNode(root, categoryPath)
        const categories = categoryNames(root, categoryPath)
        const items = categoryItems
        const creation = region()
        const listing = region()
        const folders = region()
        const treeActions = region().setAttribute("class", "etindi-tree-actions") as Region
        const listCrumbs = breadcrumbs(nav(stateName, () => open([])), [
          ...categoryPath.map((name, index) => ({title: categoryLabel(name), open: () => open(categoryPath.slice(0, index + 1))})),
        ])
        const showItemCreator = () => {
          const schema = effectiveSchema(state, root, categoryPath)
          const initial = createItem(schema, "")
          creation.show(schemaForm(schema, initial, async next => {
            validate({json: schema as any}, next)
            assert(typeof next.id === "string" && next.id.length > 0, "Item ID is required.")
            assert(!allItemIds().includes(next.id), `Item ID already exists: ${next.id}`)
            assert(typeof next.title === "string" && next.title.trim().length > 0, "Title is required.")
            const filename = slug(next.title) || slug(next.id)
            const target = ["data", ...categoryPath, `${filename}.json`]
            assert(getFile(state, target) === undefined, `An item already exists at ${target.join("/")}.`)
            if (!await tryStep(`Add item ${next.title}`, {path: target, old: null, new: JSON.stringify(next, null, 2)})) throw new Error("The item was not created.")
            await open([...categoryPath, `${filename}.json`])
            addStatus("Item created", "success")
          }, () => creation.show(), error => addStatus(String(error), "error"), {
            ...formOptions(), title: "Create item", saveLabel: "Create item",
          }))
        }
        const create = categoryPath.length >= 2
          ? showItemCreator
          : () => creation.show(createForm(
            categoryPath.length ? "Add subcategory" : "Add category", "Name", "Create category",
            name => changeTaxonomy(categoryPath, name), () => creation.show(),
            error => addStatus(String(error), "error"),
          ))
        const editItem = (target: Path, value: Record<string, unknown>, itemTitle: string) => {
          const source = getFile(state, target)
          if (source === undefined) return
          const schema = effectiveSchema(state, root, categoryPath)
          creation.show(schemaForm(schema, value, async next => {
            validate({json: schema as any}, next)
            if (!await tryStep(`Edit item ${itemTitle}`, {path: target, old: source, new: JSON.stringify(next, null, 2)})) throw new Error("The item was not saved.")
            await open(categoryPath)
            addStatus("Item saved", "success")
          }, () => creation.show(), error => addStatus(String(error), "error"), {
            ...formOptions(itemId(value)), title: "Edit item", saveLabel: "Save changes",
          }))
        }
        const deleteItem = async (target: Path, itemTitle: string) => {
          const source = getFile(state, target)
          if (source === undefined || !confirm(`Delete ${itemTitle}?`)) return
          if (await tryStep(`Delete item ${itemTitle}`, {path: target, old: source, new: null})) {
            await open(categoryPath)
            addStatus("Item deleted", "success")
          }
        }
        const actions = moduleId ? [
          circleAction(categoryPath.length >= 2 ? "Create item" : categoryPath.length ? "Add subcategory" : "Add category", "plus", create),
          treeActions,
        ] : [treeActions]
        content.show(
          explorerHeader(listCrumbs, ...actions),
          creation,
          !node && categoryPath.length ? muted("This folder is not defined in taxonomy.json.") : "",
          ...Object.entries(node ?? {}).filter(([field]) => field !== "schema" && field !== "categories" && field !== "subcategories").map(([field, value]) => {
            const look = viewConfig[field] ?? "default"
            if (look === "title") return title(String(value))
            if (look === "header") return readable(String(value))
            if (look === "html") return card(typeof value === "string" ? safeHtml(value) : wrappedCode(value))
            return card(propertyGrid([{name: categoryLabel(field), value}]))
          }),
          folders,
          listing,
        )
        updateListing = ({status, search}) => {
          const query = search.toLocaleLowerCase()
          const visibleCategories = categories.filter(category => !query || categoryLabel(category).toLocaleLowerCase().includes(query))
          const matchingItems = items.filter(({path: target, value}) => {
            if (status && value.status !== status) return false
            if (!query) return true
            const itemTitle = fieldText(presentedFields(value, "title")[0]) ?? categoryLabel(target.at(-1)!.replace(/\.json$/, ""))
            const header = fieldText(presentedFields(value, "header")[0]) ?? ""
            return `${itemTitle} ${header} ${target.join("/")}`.toLocaleLowerCase().includes(query)
          })
          const visibleIds = new Set(matchingItems.flatMap(entry => itemId(entry.value) ?? []))
          if (query || status) {
            const byId = new Map(items.flatMap(entry => itemId(entry.value) ? [[itemId(entry.value)!, entry] as const] : []))
            for (const item of matchingItems) {
              let parent = parentId(item.value)
              const seen = new Set<string>()
              while (parent && !seen.has(parent)) {
                seen.add(parent)
                visibleIds.add(parent)
                parent = byId.get(parent) ? parentId(byId.get(parent)!.value) : undefined
              }
            }
          }
          const visibleItems = query || status ? items.filter(item => {
            const id = itemId(item.value)
            return id ? visibleIds.has(id) : matchingItems.includes(item)
          }) : matchingItems
          folders.show(visibleCategories.length ? explorerFolders(visibleCategories.map(category => ({
            label: categoryLabel(category), active: false, open: () => open([...categoryPath, category]),
          }))) : "")
          const rows = nestedItems(visibleItems).map(({item: {path: target, value}}) => {
            const itemTitle = fieldText(presentedFields(value, "title")[0]) ?? categoryLabel(target.at(-1)!.replace(/\.json$/, ""))
            const comments = readComments(state, target).length
            return {
              id: itemId(value) ?? target.join("/"),
              ...(parentId(value) ? {parentId: parentId(value)} : {}),
              name: itemTitle,
              open: () => open(target.slice(1)),
              ...(moduleId ? {
                edit: () => editItem(target, value, itemTitle),
                copy: () => addStatus("Copy is not implemented yet.", "warn"),
                delete: () => deleteItem(target, itemTitle),
              } : {}),
              ...(comments ? {meta: String(comments)} : {}),
            }
          })
          listing.show(rows.length ? itemTree(rows, treeActions) : muted(status || query ? "No items match these filters." : ""))
          if (!rows.length) treeActions.show(
            circleAction("Expand all", "expand", () => {}),
            circleAction("Collapse all", "collapse", () => {}),
          )
        }
        updateListing({status: "", search: ""})
      }
      page.add(activeView)
    }

    if (path.length == 2){
      page.add(dashboard(stateName, routeUrl(path)))
    }else if (path[2] == "files"){
      showFolder(path.slice(3))
      page.add(activeView)
    }else if (path[2] == "file"){
      showFile(path.slice(3))
      page.add(activeView)
    }else if (path[2] == "agents"){
      await showAgents(path.slice(3))
    }else if (path[2] == "view"){ await showTaxonomy(path.slice(3))
    }else if (path[2] == "timeline"){showTimeline(path.slice(3)) }

  }

  if (path.length == 0) {
    const accounts = await loadAccounts()
    const content = user.id ? workspace() : narrowWorkspace()
    const uname = field("", {autocomplete: "username"})
    const upass = field("", {type: "password", autocomplete: "new-password"})

    async function login(type: "loginUser" | "createUser") {
      try {
        if (!upass.get()) throw new Error("Password is required")
        const account = {id: uname.get(), passhash: hash(upass.get())}
        await connection.functions[type](account)
        Object.assign(user, account)
        await saveCredential(account)
        upass.clear()
        await render([])
      } catch (error) {
        addStatus(error instanceof Error ? error.message : "Account login failed", "error")
      }
    }

    async function switchAccount(account: AUTH) {
      try {
        await connection.functions.loginUser(account)
        Object.assign(user, account)
        await saveCredential(account)
        await render([])
      } catch (error) {
        addStatus(`Could not switch to ${account.id}: ${error}`, "error")
      }
    }

    page.add(content)
    content.add(
      pageHeading(
        user.id ? `Welcome, ${user.id}` : "Welcome to Etindi",
        user.id ? "Open a module or create a new workspace." : "Choose an account or add another one.",
        user.id ? "grid" : "sparkles",
      ),
      card(
        title("Accounts").style({fontSize: "17px", marginTop: "0"}),
        accounts.length ? entryList(...accounts.map(account => entryRow(
          text(account.id),
          account.id === user.id
            ? muted("Current")
            : iconAction("Switch", "arrow", () => switchAccount(account)),
        ))) : muted("No saved accounts."),
        user.id ? iconAction("Log out", "arrow", async () => {
          Object.assign(user, {id: "", passhash: ""})
          await clearCurrentCredential()
          await render([])
        }) : "",
        expandable("Add account", view => view.add(
          formField("Name", uname),
          formField("Password", upass),
          toolbar(
            iconAction("Log in", "arrow", () => login("loginUser")),
            iconAction("Create account", "plus", () => login("createUser")),
          ),
        )),
      ),
    )

    if (user.id) {
      const modnamer = field()
      content.add(card(
        title("Create a module").style({fontSize: "17px", marginTop: "0"}),
        formField("Module name", modnamer),
        iconAction("Create module", "plus", async () => {
          if (!modnamer.get()) return
          await connection.functions.createModule({user, name: modnamer.get()})
          await render([])
        }),
      ))
      const modules = await connection.functions.listModules({username: user.id})
      content.add(
        title("Your modules").style({fontSize: "17px", marginTop: "28px"}),
        modules.length ? entryList(...modules.map(mod => linkRow(mod.id, routeUrl(mod.id.split("/")), "grid"))) : muted("No modules yet."),
      )
    }
  }
  else{
    if (path.length >= 2){
      let userid = path[0]!
      if (userid == "commit"){
        let commit = path[1]!
        renderState({commit})
      }else{
        let moduleName = path[1]!
        let module = await getModule(userid, moduleName)
        const siblings = await connection.query({table: "modules", field: "author", value: module.author})
        if (location.pathname.replace(/\/$/, "") !== url) return
        app.header.show(headerPicker("Select a module", siblings
          .sort((a, b) => a.id.localeCompare(b.id))
          .map(sibling => ({value: sibling.id, label: sibling.id.split("/").slice(1).join("/")})), module.id, id => {
            const compartment = path[2] === "file" ? "files" : path[2]
            void render([...id.split("/"), ...(compartment ? [compartment] : [])])
          }))
        await renderState(module)
      }
    }
  }
}

loadCredential().then(saved => {
  if (!saved) return
  Object.assign(user, saved)

}).catch(() => {})

.then(()=>{
  window.addEventListener("popstate", () => { void render(routePath(), false) })
  void render(routePath(), false)
})
